import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { RETRYABLE_ERROR_CODES, getAssistantErrorLocaleKey } from '../../constants';

import BBCode from '/b2b/common/components/Locale/formatters/BBCode';
import ChatInput from './components/ChatInput';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Locale from '/b2b/common/components/Locale';
import MessageList from './components/MessageList';
import PropTypes from 'prop-types';
import React from 'react';
import SourceBanner from './components/SourceBanner';
import ThinkingIndicator from './components/ThinkingIndicator';
import classNames from 'classnames';
import fromState from '/b2b/common/state/selectors';
import makeStyles from '@mui/styles/makeStyles';
import styles from './ChatThread.styles';
import { translate } from '/b2b/common/helpers/i18n';
import useAIAssistant from '../../useAIAssistant';
import { useSelector } from 'react-redux';

const useStyles = makeStyles(styles);

// const SUGGESTED_PROMPTS = [
//     'aiAssistant.newChat.suggestedPrompt1',
//     'aiAssistant.newChat.suggestedPrompt2',
//     'aiAssistant.newChat.suggestedPrompt3',
// ];

const ChatThread = props => {
    const {
        className,
        sendMessagePlaceholder,
        isReadOnly = false,
        readOnlyMessage,
        readOnlySeverity = 'warning',
        // Locale key for the conversation's escalation state, drawn in the transcript.
        stateNoticeKey,
        escalationEvents,
        onOpenConversation,
        // Stacks the greeting indicator above the title for narrow panel widths.
        compact = false,
        onNavigate,
    } = props;
    const classes = useStyles(props);
    const {
        activeConversation,
        activeMessages,
        activeMessagePagination,
        isStreaming,
        isLoadingConversation,
        isLoadingEarlierMessages,
        error,
        sendMessage,
        sendFeedback,
        loadEarlierMessages,
        activeConversationId,
    } = useAIAssistant();
    const userId = useSelector(fromState.Auth.user.getId);
    const currentUser = useSelector(state => fromState.DB.user.getEntityById(state, userId));
    const firstName = currentUser?.firstName;

    const hasError = !!error;
    const hasMessages = activeMessages.length > 0;
    const errorLocaleKey = getAssistantErrorLocaleKey(error?.errorCode);
    /*
     * chat-api tags every conversation `ai`, escalation-born ones included, so the
     * escalation is what marks an escalated thread.
     */
    const sourceAiConversationId = activeConversation?.escalation?.sourceConversationId || null;

    const isRetryableError = RETRYABLE_ERROR_CODES.includes(error?.errorCode);
    const lastUserMessage = isRetryableError
        ? [...activeMessages].reverse().find(m => m.role === 'user')
        : null;

    const renderContent = () => {
        if (isLoadingConversation) {
            return (
                <Box className={classes.centerContainer}>
                    <CircularProgress size={32} />
                </Box>
            );
        }

        if (hasError && !hasMessages) {
            return (
                <Box className={classes.centerContainer}>
                    <ErrorOutlineIcon className={classes.errorIcon} />
                    <Typography variant="h2" color="textSecondary">
                        <Locale path="aiAssistant.errors.title" />
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        <Locale path={errorLocaleKey} />
                    </Typography>
                </Box>
            );
        }

        if (!hasMessages) {
            return (
                <Box className={classes.centerContainer}>
                    <Box
                        className={classNames(classes.emptyTitleRow, {
                            [classes.emptyTitleRowCompact]: compact,
                        })}
                    >
                        {compact ? (
                            <Box className={classes.brandLockup}>
                                <ThinkingIndicator size={24} />
                                <Typography variant="h6" component="span">
                                    {translate('aiAssistant.name')}
                                </Typography>
                            </Box>
                        ) : (
                            <ThinkingIndicator size={28} />
                        )}
                        <Typography variant={compact ? 'h3' : 'h2'} className={classes.emptyTitle}>
                            {firstName ? (
                                <Locale
                                    path="aiAssistant.newChat.personalizedTitle"
                                    options={{ firstName }}
                                />
                            ) : (
                                <Locale path="aiAssistant.newChat.defaultTitle" />
                            )}
                        </Typography>
                    </Box>
                    <Box className={classes.emptyInput}>
                        <ChatInput
                            onSend={sendMessage}
                            isStreaming={isStreaming}
                            disabled={false}
                            sendMessagePlaceholder={sendMessagePlaceholder}
                            compact={compact}
                        />
                        {/* TODO: Re-enable when we have prompts that have been well tested against AI Assistant */}
                        {/* <Box className={classes.suggestedPrompts}>
                            {SUGGESTED_PROMPTS.map(promptKey => (
                                <Button
                                    key={promptKey}
                                    variant="outlined"
                                    size="small"
                                    className={classes.suggestedPrompt}
                                    onClick={() => sendMessage(translate(promptKey))}
                                >
                                    <Locale path={promptKey} />
                                </Button>
                            ))}
                        </Box> */}
                        <Typography
                            variant="caption"
                            color="text.hint"
                            className={classes.disclaimer}
                        >
                            <Locale
                                formatter={BBCode}
                                path="aiAssistant.disclaimer"
                                options={{ aiName: translate('aiAssistant.name') }}
                            />
                        </Typography>
                    </Box>
                </Box>
            );
        }

        return (
            <>
                <MessageList
                    messages={activeMessages}
                    isStreaming={isStreaming}
                    onFeedback={sendFeedback}
                    hasEarlierMessages={activeMessagePagination?.hasNext || false}
                    isLoadingEarlierMessages={isLoadingEarlierMessages}
                    onLoadEarlier={() => loadEarlierMessages(activeConversationId)}
                    escalationEvents={escalationEvents}
                    stateNoticeKey={stateNoticeKey}
                    onOpenConversation={onOpenConversation}
                    onNavigate={onNavigate}
                />
                <Box className={classes.inputWrapper}>
                    <Box className={classes.inputContent}>
                        {sourceAiConversationId && (
                            <SourceBanner
                                aiConversationId={sourceAiConversationId}
                                onOpenConversation={onOpenConversation}
                            />
                        )}
                        {hasError && (
                            <Alert
                                severity="error"
                                className={classes.escalatedBanner}
                                action={
                                    isRetryableError && lastUserMessage ? (
                                        <Button
                                            color="inherit"
                                            size="small"
                                            onClick={() => sendMessage(lastUserMessage.content)}
                                        >
                                            <Locale path="common.retry" />
                                        </Button>
                                    ) : undefined
                                }
                            >
                                <Locale path={errorLocaleKey} />
                            </Alert>
                        )}
                        {isReadOnly && readOnlyMessage && (
                            <Alert severity={readOnlySeverity} className={classes.escalatedBanner}>
                                {readOnlyMessage}
                            </Alert>
                        )}
                        <ChatInput
                            onSend={sendMessage}
                            isStreaming={isStreaming}
                            disabled={isReadOnly}
                            // A disabled composer still prompting for a question reads as broken.
                            sendMessagePlaceholder={
                                isReadOnly
                                    ? 'aiAssistant.inputs.message.readOnlyPlaceholder'
                                    : sendMessagePlaceholder
                            }
                            compact={compact}
                        />
                        <Typography
                            variant="caption"
                            color="text.hint"
                            className={classes.disclaimer}
                        >
                            <Locale
                                formatter={BBCode}
                                path="aiAssistant.disclaimer"
                                options={{ aiName: translate('aiAssistant.name') }}
                            />
                        </Typography>
                    </Box>
                </Box>
            </>
        );
    };

    return <Box className={`${classes.root} ${className || ''}`}>{renderContent()}</Box>;
};

ChatThread.propTypes = {
    className: PropTypes.string,
    sendMessagePlaceholder: PropTypes.string,
    isReadOnly: PropTypes.bool,
    readOnlyMessage: PropTypes.node,
    readOnlySeverity: PropTypes.oneOf(['success', 'info', 'warning', 'error']),
    stateNoticeKey: PropTypes.string,
    escalationEvents: PropTypes.array,
    onOpenConversation: PropTypes.func,
    compact: PropTypes.bool,
    onNavigate: PropTypes.func,
};

export default ChatThread;
